import React, { useState } from 'react';
import './ContactForm3.css'; 
import ReCAPTCHA from "react-google-recaptcha"; // Commented out due to compilation error in this environment
// import './ContactForm2.css'; // Commented out; critical CSS embedded below
// import LoadingOverlay from './LoadingOverlay.js'; // Commented out; component embedded below

// --- Embedded LoadingOverlay Component (formerly LoadingOverlay.js) ---
const LoadingOverlay = ({ isLoading, message, subMessage }) => {
    if (!isLoading) return null;

    return (
        <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            zIndex: 1000,
            borderRadius: '8px' // Match parent form's border-radius
        }}>
            <div style={{
                border: '4px solid rgba(255, 255, 255, 0.3)',
                borderTop: '4px solid #fff',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '15px', fontSize: '1.2em', fontWeight: 'bold' }}>{message}</p>
            {subMessage && <p style={{ fontSize: '0.9em', opacity: 0.8 }}>{subMessage}</p>}

            {/* Keyframes for spin animation, directly in style or a separate global style block */}
            <style>
                {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
};

// --- Main ContactForm Component ---
function ContactForm({ hasAcceptedTrackingCookies }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    // Renamed from isVerified to recaptchaValue to clearly hold the token
    const [recaptchaValue, setRecaptchaValue] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    // It's good that you are using environment variables for site key
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeulY8qAAAAAFoGxOKmImDUVoQmfat_xvrtzBdc";

    // Placeholder for handleRecaptchaChange since ReCAPTCHA component is commented out.
    // In a real deployment, you'd integrate the Google reCAPTCHA JS and get the token here.
    const handleRecaptchaChange = (value) => {
        setRecaptchaValue(value); // Store the actual reCAPTCHA token value
        // For testing without the widget, you might uncomment the line below to simulate verification:
        // setRecaptchaValue("test_recaptcha_token_simulate_success");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!name || !email || !message) {
            setError('All fields are required.');
            return;
        }

        // Check if reCAPTCHA value exists before proceeding
        // This check is crucial; if you're not using the reCAPTCHA widget,
        // you might want to adjust how recaptchaValue is set (e.g., to a default "ok" value for testing).
        if (!recaptchaValue) {
            setError("Please verify you're not a robot by completing the reCAPTCHA (or ensure it's loaded).");
            return;
        }

        setIsLoading(true);
        setLoadingMessage('Sending your message...');

        try {
            // Corrected: Always use REACT_APP_BACKEND_URL.
            // Ensure REACT_APP_BACKEND_URL is set in .env.development for local testing to point to your live hosted PHP.
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            // Log the URL that will be used for the fetch request
            console.log("Using backendUrl:", backendUrl);

            // The API endpoint where your PHP script will be located
            const apiUrl = `${backendUrl}/api/contact.php`;
            console.log("Sending request to:", apiUrl);

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                // Send the reCAPTCHA token along with other form data
                body: JSON.stringify({ name, email, message, recaptchaToken: recaptchaValue }),
            });

            console.log("Response status:", response.status);
            const responseText = await response.text();
            console.log("Raw response text:", responseText);

            try {
                const jsonResponse = JSON.parse(responseText);
                console.log("Parsed JSON response:", jsonResponse);

                if (response.ok && jsonResponse.success) { // Check both HTTP status and API's 'success' field
                    setSuccess(true);
                    setName('');
                    setEmail('');
                    setMessage('');
                    setRecaptchaValue(null); // Reset reCAPTCHA after successful submission
                    setLoadingMessage('Message sent successfully!');
                    setTimeout(() => {
                        setIsLoading(false);
                        setLoadingMessage('');
                    }, 2000);
                } else {
                    const errorMessage = jsonResponse.message || jsonResponse.error || 'Failed to send message from server.';
                    setError(errorMessage);
                    setLoadingMessage('');
                    setIsLoading(false);
                }
            } catch (jsonParseError) {
                const errorMessage = `Server returned invalid response (Status: ${response.status}). Response starts with: ${responseText.substring(0, 200)}...`;
                setError(errorMessage);
                setLoadingMessage('');
                setIsLoading(false);
                console.error("Failed to parse JSON response:", jsonParseError);
            }

        } catch (err) {
            setError(err.message || 'Network error. Please check your internet connection.');
            setLoadingMessage('');
            setIsLoading(false);
            console.error("Fetch error or custom error:", err);
        }
    };

    const subMessage = loadingMessage === 'Sending your message...'
                        ? 'Please do not close this page.'
                        : '';

    return (
        <> {/* Added a Fragment to wrap the style tag and the form */}
            {/* --- Embedded CSS (formerly ContactForm2.css) --- */}
            

            <form onSubmit={handleSubmit} className="contact-form-container relative">
                <LoadingOverlay
                    isLoading={isLoading}
                    message={loadingMessage}
                    subMessage={subMessage}
                />

                <h2>Contact Us</h2>
                {success && <p className="success-message">Your message has been sent successfully!</p>}
                {error && <p className="error-message" style={{ color: 'red' }}>{error}</p>}

                <div className="form-container">
                    <div className="form-group">
                        <label className="form-label" htmlFor="name">Name:</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            className="form-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="email">Email:</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            className="form-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label" htmlFor="message">Message:</label>
                        <textarea
                            id="message"
                            name="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="form-input message"
                            required
                            disabled={isLoading}
                        />
                    </div>
                </div>

                <div className="form-group" style={{ textAlign: 'center', marginTop: '20px' }}>
                    <ReCAPTCHA
                        sitekey={recaptchaSiteKey} // Uses your REACT_APP_RECAPTCHA_SITE_KEY from .env
                        onChange={handleRecaptchaChange}
                        onExpired={() => setRecaptchaValue(null)} // Clear token on expiry
                        onErrored={() => setRecaptchaValue(null)} // Clear token on error
                        // You can add size="compact" or theme="dark" if desired
                    />
                </div>


                <button className="form-submit" type="submit" disabled={isLoading}>Submit</button>
            </form>
        </>
    );
}

export default ContactForm;
